// web/src/features/team/player/hooks/useRosterPlayer.ts
'use memo'

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useSelector } from 'src/hooks/useRedux'
import { getPlayerTypesBySportId } from '@sportninja/common/selectors/types'
import { resolveDefaultPositionId } from '@sportninja/common/features/team/player'
import { queryKeys } from '@sportninja/common/config/queryKeys'
import {
  createRosterPlayer,
  copyRosterPlayer,
  updateRosterPlayer,
  uploadRosterPlayerImage,
  deleteRosterPlayerImage,
  type RosterCreatePayload,
} from '@sportninja/common/services/team/rosterPlayer'

interface PlayerType {
  id: string | number
  name_full?: string
  name?: string
  is_default?: number | boolean
}

/** Position dropdown options for the roster's sport, from the Redux types store. */
export const usePositionOptions = (
  sportId: string | null
): Array<{ label: string; value: string }> => {
  const playerTypes = useSelector<PlayerType[]>((state) =>
    getPlayerTypesBySportId(state, sportId)
  )
  return (playerTypes ?? []).map((p) => ({
    label: p.name_full ?? p.name ?? String(p.id),
    value: String(p.id),
  }))
}

/**
 * The sport's default position id (`is_default`, else the first type), used to
 * pre-select Position when ADDING a player so the save never sends
 * player_type_id: null. '' when the sport has no types.
 */
export const useDefaultPositionId = (sportId: string | null): string => {
  const playerTypes = useSelector<PlayerType[]>((state) =>
    getPlayerTypesBySportId(state, sportId)
  )
  return resolveDefaultPositionId(playerTypes)
}

/** POST a new roster player. Invalidates the (RQ) roster players key on success. */
export const useCreateRosterPlayer = () => {
  const queryClient = useQueryClient()
  const mutation = useMutation<
    { id: string } & Record<string, unknown>,
    Error,
    { teamId: string; rosterId: string; body: RosterCreatePayload }
  >({
    mutationFn: ({ teamId, rosterId, body }) =>
      createRosterPlayer(teamId, rosterId, body),
    retry: 0,
    onSuccess: (_data, { teamId, rosterId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.teamRosterPlayers.byRoster(teamId, rosterId),
      })
    },
  })
  return { create: mutation.mutateAsync, isPending: mutation.isPending }
}

/**
 * PUT an EXISTING player onto a roster via the copy endpoint (full payload).
 * Invalidates the roster players key on success.
 */
export const useCopyRosterPlayer = () => {
  const queryClient = useQueryClient()
  const mutation = useMutation<
    { id: string } & Record<string, unknown>,
    Error,
    {
      teamId: string
      rosterId: string
      playerId: string
      body: RosterCreatePayload
    }
  >({
    mutationFn: ({ teamId, rosterId, playerId, body }) =>
      copyRosterPlayer(teamId, rosterId, playerId, body),
    retry: 0,
    onSuccess: (_data, { teamId, rosterId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.teamRosterPlayers.byRoster(teamId, rosterId),
      })
    },
  })
  return { copy: mutation.mutateAsync, isPending: mutation.isPending }
}

/**
 * PUT to edit an existing roster player (full payload). Invalidates the roster
 * players key on success.
 */
export const useUpdateRosterPlayer = () => {
  const queryClient = useQueryClient()
  const mutation = useMutation<
    { id: string } & Record<string, unknown>,
    Error,
    {
      teamId: string
      rosterId: string
      playerId: string
      body: RosterCreatePayload
    }
  >({
    mutationFn: ({ teamId, rosterId, playerId, body }) =>
      updateRosterPlayer(teamId, rosterId, playerId, body),
    retry: 0,
    onSuccess: (_data, { teamId, rosterId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.teamRosterPlayers.byRoster(teamId, rosterId),
      })
    },
  })
  return { update: mutation.mutateAsync, isPending: mutation.isPending }
}

/** POST/DELETE the team-scoped player image (legacy route). */
export const useRosterPlayerImageMutation = () => {
  const upload = useMutation<
    unknown,
    Error,
    { teamId: string; playerId: string; file: File | string }
  >({
    mutationFn: ({ teamId, playerId, file }) =>
      uploadRosterPlayerImage(teamId, playerId, file),
    retry: 0,
  })
  const remove = useMutation<
    unknown,
    Error,
    { teamId: string; playerId: string }
  >({
    mutationFn: ({ teamId, playerId }) =>
      deleteRosterPlayerImage(teamId, playerId),
    retry: 0,
  })
  return {
    uploadImage: upload.mutateAsync,
    deleteImage: remove.mutateAsync,
    isPending: upload.isPending || remove.isPending,
  }
}
